import {
  AlertType,
  DismissedAlert,
  Tkv2OrderAttachmentBody,
  dismissedAlertMatches,
  isDefined,
  nonDismissibleAlertTypes,
} from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AlertsState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export type DismissAlertPayload = { type: AlertType; propertyID?: string };

const toDismissedAlert = ({ type, propertyID }: DismissAlertPayload): DismissedAlert =>
  propertyID ? { type, propertyID } : type;

export const initialAlertsState: AlertsState = {
  dismissedAlerts: [],
};

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: initialAlertsState,
  reducers: {
    dismissAlert: (state, action: PayloadAction<DismissAlertPayload>) => {
      if (nonDismissibleAlertTypes.includes(action.payload.type)) return;
      const entry = toDismissedAlert(action.payload);
      if (state.dismissedAlerts.some((dismissed) => dismissedAlertMatches(dismissed, action.payload)))
        return;

      Object.assign(state, {
        dismissedAlerts: [...state.dismissedAlerts, entry],
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    cancelDismissAlert: (state, action: PayloadAction<DismissAlertPayload>) => {
      if (!state.dismissedAlerts.some((dismissed) => dismissedAlertMatches(dismissed, action.payload)))
        return;

      Object.assign(state, {
        dismissedAlerts: state.dismissedAlerts.filter(
          (dismissed) => !dismissedAlertMatches(dismissed, action.payload),
        ),
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    setAlerts: (state, action: PayloadAction<Tkv2OrderAttachmentBody<'alerts'>>) => {
      Object.assign(state, {
        ...action.payload,
      });
    },
    alertsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.syncState)) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialAlertsState);
  },
});

export const { dismissAlert, cancelDismissAlert, setAlerts, alertsMutationComplete } =
  alertsSlice.actions;

export const alertsReducer = alertsSlice.reducer;
